import {useCallback, useEffect, useMemo, useRef, type SyntheticEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import useShow from '../../../hooks/useShow';
import useRestartXHR from '../../../hooks/useRestartXHR';
import setGlobalState, {resetGlobalState} from '../../../redux/setGlobalState';
import {XHR_LOADING_STATE} from '../../../assets/constants/constants';
import type {TXHR} from '../../../assets/constants/constansts.type';
import type {TState} from '../../../redux/store.type';
import {createAiAgentSubmitHandler} from '../utils/aiAgentSubmit';
import {clearHeldProposal} from '../utils/agentProposalHold';
import {useAiAgentAsk} from './useAiAgentAsk';

export function useAiAgentPanel() {
    const show = useShow();
    const {t} = useTranslation('aiAgent');
    const navigate = useNavigate();
    const isOpen = useSelector((s: TState) => s.aiAgent?.isOpen);
    const sendMessageXHR = useSelector((s: TState) => s.aiAgent?.sendMessageXHR);
    const executeProposalXHR = useSelector((s: TState) => s.aiAgent?.executeProposalXHR);
    const pendingProposal = useSelector((s: TState) => s.aiAgent?.pendingProposal);

    const turnXHR: TXHR = executeProposalXHR === XHR_LOADING_STATE
        ? XHR_LOADING_STATE
        : sendMessageXHR ?? null;

    const submitHandler = useMemo(
        () => createAiAgentSubmitHandler(navigate, {
            imageTooLarge: t('imageTooLarge'),
            imageUnsupported: t('imageUnsupported'),
            actionFailed: t('actionFailed'),
            createdOpenFailed: t('createdOpenFailed'),
            proposalConfirmed: t('proposalConfirmed'),
            proposalAlreadyCreated: t('proposalAlreadyCreated'),
            proposalOtherOwner: t('proposalOtherOwner'),
        }),
        [navigate, t],
    );
    const {pendingAsk, clearAsk, handleSubmit} = useAiAgentAsk(submitHandler);
    useRestartXHR(sendMessageXHR, 'aiAgent.sendMessageXHR');
    useRestartXHR(executeProposalXHR, 'aiAgent.executeProposalXHR');

    // useShow returns a fresh object every render, so `show` itself must stay out of the
    // dependency list — a ref keeps the effect honest without re-running it each render.
    const showRef = useRef(show);
    showRef.current = show;
    const isPanelOpen = show.isOpen;
    const isPanelVisible = !!show.showClass;

    useEffect(() => {
        if(isOpen && !isPanelOpen) {
            showRef.current.open();
            return;
        }
        if(!isOpen && isPanelVisible) {
            void showRef.current.close();
        }
    }, [isOpen, isPanelOpen, isPanelVisible]);

    const openAgent = useCallback(() => {
        setGlobalState('aiAgent.isOpen', true);
        show.open();
    }, [show]);

    const closeAgent = useCallback(async (e?: Event | SyntheticEvent) => {
        setGlobalState('aiAgent.isOpen', false);
        // A ticket whose page never mounted must not pop up on a later visit.
        resetGlobalState('aiAgent.pendingTicketTid');
        await show.close(e);
    }, [show]);

    const handleReset = useCallback(() => {
        clearAsk();
        clearHeldProposal();
        setGlobalState('aiAgent.sessionEventType', null);
        setGlobalState('aiAgent.sessionIntent', null);
        setGlobalState('aiAgent.chatLang', null);
        setGlobalState('aiAgent.activeFormId', null);
        setGlobalState('aiAgent.createdDraftFields', null);
        setGlobalState('aiAgent.currentFormValues', null);
        resetGlobalState('aiAgent.pendingTicketTid');
    }, [clearAsk]);

    const agentShow = useMemo(() => ({
        ...show,
        open: openAgent,
        close: closeAgent,
    }), [show, openAgent, closeAgent]);

    return {
        t,
        agentShow,
        openAgent,
        handleReset,
        handleSubmit,
        pendingAsk,
        clearAsk,
        pendingProposal: pendingProposal ?? null,
        turnXHR,
    };
}
